import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { TRACE_LEVELS, type TraceLevel } from '@ai-studio/shared';

import { categoryCounts, filterRows, parseTypeList, rowJson, rowText, type RowLevel, type TraceRow } from '../../lib/game-trace.js';
import { useGameTrace } from '../../state/game-trace.js';
import { usePlay } from '../../state/play.js';
import { IconButton } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import './game-console.css';

/** Rendered rows at most (the newest matching ones); the buffer holds more. */
const SHOWN_MAX = 1500;

const LEVEL_HINT: Record<TraceLevel, string> = {
  errors: 'Errors and warnings only',
  normal: 'Lifecycle, collisions/triggers, input actions, scripts, errors',
  verbose: 'Normal + component changes, timers, animation, audio, UI, throttled transforms',
  everything: 'Everything, including raw input, every transform and per-frame perf',
};

function copy(text: string): void {
  void navigator.clipboard?.writeText(text).catch(() => undefined);
}

/**
 * Game console: everything the running game reports through the engine trace
 * (the same stream the assistant reads with read_game_trace), with type
 * chips, entity / text / type filters, pause, clear and copy as JSON. Clicking
 * a row with an entity selects it in the live Inspector.
 */
export function GameConsole({ onSelectEntity, actions }: { onSelectEntity?: (id: string) => void; actions?: ReactNode }) {
  const rows = useGameTrace((s) => s.rows);
  const overflow = useGameTrace((s) => s.overflow);
  const support = useGameTrace((s) => s.support);
  const level = useGameTrace((s) => s.config.level);
  const unsent = useGameTrace((s) => s.unsent);
  const playState = usePlay((s) => s.state);
  const [paused, setPaused] = useState(false);
  const [frozen, setFrozen] = useState<TraceRow[] | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [entity, setEntity] = useState('');
  const [text, setText] = useState('');
  const [types, setTypes] = useState('');
  const [show, setShow] = useState<RowLevel>('all');
  const [selected, setSelected] = useState<number | null>(null);
  const logRef = useRef<HTMLDivElement | null>(null);

  const source = paused && frozen ? frozen : rows;
  const counts = useMemo(() => categoryCounts(source), [source]);
  const matching = useMemo(
    () => filterRows(source, { hidden, entity: entity.trim(), text: text.trim(), types: parseTypeList(types), level: show }),
    [source, hidden, entity, text, types, show],
  );
  const visible = matching.length > SHOWN_MAX ? matching.slice(-SHOWN_MAX) : matching;
  const current = selected === null ? null : (source.find((r) => r.key === selected) ?? null);

  // Follow the end unless the user scrolled up.
  useEffect(() => {
    const log = logRef.current;
    if (!log || paused) return;
    const nearEnd = log.scrollHeight - log.scrollTop - log.clientHeight < 80;
    if (nearEnd) log.scrollTop = log.scrollHeight;
  }, [visible.length, paused, rows]);

  const togglePause = () => {
    setFrozen(paused ? null : rows);
    setPaused(!paused);
  };

  const toggleChip = (category: string) =>
    setHidden((h) => {
      const next = new Set(h);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });

  const running = playState === 'playing' || playState === 'paused';

  return (
    <div className="console game-console">
      <div className="console-bar">
        <IconButton
          icon="trash"
          title="Clear the game console"
          onClick={() => {
            useGameTrace.getState().clear();
            setFrozen(paused ? [] : null);
            setSelected(null);
          }}
        />
        <IconButton icon={paused ? 'play' : 'pause'} title={paused ? 'Resume the view (still recording)' : 'Pause the view (keeps recording)'} active={paused} onClick={togglePause} />
        <IconButton icon="copy" title="Copy the visible events as JSON" onClick={() => copy(rowJson(visible))} />
        <label className="gt-level" title={LEVEL_HINT[level]}>
          <span className="muted">Trace</span>
          <select value={level} onChange={(e) => useGameTrace.getState().setLevel(e.target.value as TraceLevel)} aria-label="Trace level">
            {TRACE_LEVELS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label className="gt-level" title="Which rows to show">
          <span className="muted">Show</span>
          <select value={show} onChange={(e) => setShow(e.target.value as RowLevel)} aria-label="Rows to show">
            <option value="all">all</option>
            <option value="problems">warnings + errors</option>
            <option value="errors">errors</option>
          </select>
        </label>
        <div className="search-input compact" title="Entity id or name (also the other party of a collision)">
          <Icon name="target" size={12} />
          <input value={entity} onChange={(e) => setEntity(e.target.value)} placeholder="Entity" aria-label="Filter by entity" />
        </div>
        <div className="search-input compact" title="Type prefixes, comma separated (physics.trigger, input)">
          <Icon name="tag" size={12} />
          <input value={types} onChange={(e) => setTypes(e.target.value)} placeholder="Types" aria-label="Filter by type prefix" />
        </div>
        <div className="search-input compact">
          <Icon name="search" size={12} />
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Text" aria-label="Filter by text" />
        </div>
        <span className="bar-spacer" />
        <span className="muted mono gt-count" title={`${source.length} events held${overflow ? `, ${overflow} older ones dropped` : ''}${unsent ? `, ${unsent} not sent to the assistant` : ''}`}>
          {matching.length}/{source.length}
        </span>
        {actions}
      </div>
      {counts.length > 0 && (
        <div className="gt-chips" role="toolbar" aria-label="Event types">
          {counts.map(([category, n]) => (
            <button
              key={category}
              type="button"
              className={`gt-chip gt-c-${category}${hidden.has(category) ? '' : ' is-on'}`}
              aria-pressed={!hidden.has(category)}
              onClick={() => toggleChip(category)}
              title={`${hidden.has(category) ? 'Show' : 'Hide'} ${category} events`}
            >
              {category} <span className="mono">{n}</span>
            </button>
          ))}
        </div>
      )}
      {support === 'unsupported' && (
        <p className="gt-banner" role="status">
          <Icon name="warning" size={13} /> This engine build has no game trace. Rebuild the engine (pnpm engine:build) to see
          everything that happens in the game here and to let the assistant read it.
        </p>
      )}
      <div className="console-log gt-log" role="log" ref={logRef}>
        {visible.length === 0 && (
          <p className="console-empty muted">
            {source.length > 0
              ? 'No events match the filters.'
              : running
                ? 'Waiting for game events…'
                : 'Press Play: collisions, inputs, script logs and errors, spawns and state changes show up here.'}
          </p>
        )}
        {matching.length > visible.length && (
          <p className="muted gt-more">{matching.length - visible.length} older matching events not shown; narrow the filters to see them.</p>
        )}
        {visible.map((row) => {
          const r = rowText(row);
          return (
            <div
              key={row.key}
              className={`gt-row gt-c-${r.category}${selected === row.key ? ' is-selected' : ''}`}
              onClick={() => {
                setSelected(row.key);
                if (row.entity && onSelectEntity) onSelectEntity(row.entity);
              }}
              title={row.entity ? `${row.entity} — click to inspect` : undefined}
            >
              <span className="gt-time mono">{r.time}</span>
              <span className="gt-frame mono muted">{r.frame}</span>
              <span className="gt-type mono">{r.type}</span>
              {r.subject && <span className="gt-subject">{r.subject}</span>}
              {r.data && <span className="gt-data mono">{r.data}</span>}
            </div>
          );
        })}
      </div>
      {current && (
        <div className="console-details gt-details">
          <div className="console-details-head">
            <span className="mono">{current.type}</span>
            <span className="muted mono">
              t={current.t.toFixed(3)} f={current.f} seq={current.seq}
            </span>
            <span className="bar-spacer" />
            <button type="button" className="ghost small" onClick={() => copy(rowJson(current))} title="Copy this event as JSON">
              <Icon name="copy" size={12} /> Copy JSON
            </button>
            <IconButton icon="x" title="Close" onClick={() => setSelected(null)} />
          </div>
          <pre className="console-details-text">{rowJson(current)}</pre>
        </div>
      )}
    </div>
  );
}
